import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { createAbsensi, getServerTime, getSekolah, getSekolahList, getFaceReference, getTodayAttendanceStatus, getInstansi } from '../../lib/api';
import { addToAttendanceQueue, getPendingCount, getCachedFaceDescriptor, getOfflineTodayStatus, cacheTodayAttendance } from '../../lib/db';
import { compressImage, calculateDistance } from '../../lib/utils';
import { Button } from '../ui/button';
import { ShieldCheck, X } from 'lucide-react';
import { toast } from '../ui/sonner';
import { useGpsLocation, GPS_CONFIG } from '../../hooks/useNativeGps';
import { useCamera } from '../../hooks/useNativeCamera';
import { useNativePlatform } from '../../hooks/useNativePlatform';

// Sub-components
import { CameraSection } from '../absensi/CameraSection';
import { GpsSection } from '../absensi/GpsSection';
import { FaceVerificationBadge, DesktopWarning, OfflineWarning, FakeGpsAlert, TodayStatusAlert, DuplicateWarnings } from '../absensi/AttendanceAlerts';
import { AbsenTypeSelector } from '../absensi/AbsenTypeSelector';
import { FaceVerificationScreen } from '../absensi/FaceVerificationScreen';
import { ActionButtons } from '../absensi/ActionButtons';
import { SchoolSelector } from '../absensi/SchoolSelector';
import { GpsSecurityTips } from '../absensi/GpsSecurityTips';
import { ServerTimeCard } from '../absensi/ServerTimeCard';
import { FaceRegistrationBlock } from '../absensi/FaceRegistrationBlock';

const AbsensiPage = () => {
  const { user, isOnline } = useAuth();
  const navigate = useNavigate();

  // Role flags
  const isPengawas = user?.role === 'pengawas';
  const isPegawaiInstansi = user?.role === 'pegawai_instansi';

  // Data states
  const [sekolah, setSekolah] = useState(null);
  const [instansi, setInstansi] = useState(null);
  const [sekolahList, setSekolahList] = useState([]);
  const [selectedSekolahId, setSelectedSekolahId] = useState(null);
  const [serverTime, setServerTime] = useState(null);
  const [todayStatus, setTodayStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);

  // Face verification states
  const [faceData, setFaceData] = useState(null);
  const [loadingFace, setLoadingFace] = useState(true);
  const [showFaceVerification, setShowFaceVerification] = useState(false);
  const [faceVerified, setFaceVerified] = useState(false);
  const [offlineFaceDescriptor, setOfflineFaceDescriptor] = useState(null);
  const [faceMatchScore, setFaceMatchScore] = useState(null);

  // Face registration grace period
  const [faceRegistrationStatus, setFaceRegistrationStatus] = useState(null);

  // Submission & absen type
  const [submitting, setSubmitting] = useState(false);
  const [absenType, setAbsenType] = useState('masuk');

  // Device / platform detection (Capacitor-aware)
  const { isMobileDevice, isNative, platform } = useNativePlatform();

  // Target location for GPS distance calculation
  const targetLocation = isPegawaiInstansi ? instansi : sekolah;

  // Custom hooks
  const camera = useCamera();
  const gps = useGpsLocation({ isMobileDevice, targetLocation });

  // Load today's attendance status
  useEffect(() => {
    const checkTodayStatus = async () => {
      if (!user) { setLoadingStatus(false); return; }
      try {
        let status;
        if (isOnline) {
          status = await getTodayAttendanceStatus();
          setTodayStatus(status);
          // Capture face registration status from backend
          if (status.face_status) {
            setFaceRegistrationStatus(status.face_status);
          }
          if (status.masuk_data) await cacheTodayAttendance(status.masuk_data);
          if (status.pulang_data) await cacheTodayAttendance(status.pulang_data);
        } else {
          status = await getOfflineTodayStatus();
          setTodayStatus(status);
        }
        if (status.has_masuk && !status.has_pulang) {
          setAbsenType('pulang');
          toast.info(status.message, { duration: 5000 });
        } else if (status.has_masuk && status.has_pulang) {
          toast.success(status.message, { duration: 5000 });
        }
      } catch (error) {
        console.error('Error checking today status:', error);
        if (!isOnline || error.message?.includes('Network')) {
          try {
            const offlineStatus = await getOfflineTodayStatus();
            setTodayStatus(offlineStatus);
            if (offlineStatus.has_masuk && !offlineStatus.has_pulang) setAbsenType('pulang');
          } catch (e) { console.error('Offline status check also failed:', e); }
        }
      } finally { setLoadingStatus(false); }
    };
    checkTodayStatus();
  }, [user, isOnline]);

  // Load school/instansi data, server time, and face data
  useEffect(() => {
    const loadData = async () => {
      try {
        if (isPegawaiInstansi && user?.instansi_id) {
          const instansiData = await getInstansi(user.instansi_id);
          setInstansi(instansiData);
        } else if (isPengawas && user?.sekolah_ids?.length > 0) {
          const allSchools = await getSekolahList();
          const supervisedSchools = allSchools.filter(s => user.sekolah_ids.includes(s.id));
          setSekolahList(supervisedSchools);
          if (supervisedSchools.length === 1) {
            setSelectedSekolahId(supervisedSchools[0].id);
            setSekolah(supervisedSchools[0]);
          }
        } else if (user?.sekolah_id) {
          const schoolData = await getSekolah(user.sekolah_id);
          setSekolah(schoolData);
          setSelectedSekolahId(user.sekolah_id);
        }

        if (isOnline) {
          const timeData = await getServerTime();
          setServerTime(new Date(timeData.server_time));
        } else {
          setServerTime(new Date());
        }

        if (isOnline) {
          try {
            const faceRefData = await getFaceReference();
            setFaceData(faceRefData);
          } catch (e) { console.log('Face reference not available from server'); }
        }

        try {
          const cachedFace = await getCachedFaceDescriptor();
          if (cachedFace && cachedFace.descriptor && cachedFace.userId === user?.id) {
            setOfflineFaceDescriptor(cachedFace.descriptor);
            if (!isOnline && !faceData) {
              setFaceData({ has_reference: true, face_descriptor: cachedFace.descriptor });
            }
          }
        } catch (e) { console.log('No cached face descriptor available'); }

        setLoadingFace(false);
      } catch (error) {
        console.error('Failed to load data:', error);
        setServerTime(new Date());
        setLoadingFace(false);
      }
    };
    loadData();
  }, [user, isOnline, isPengawas, isPegawaiInstansi]);

  // Initialize camera and location
  useEffect(() => {
    camera.initCamera();
    gps.getLocation();

    const interval = setInterval(() => {
      setServerTime(prev => prev ? new Date(prev.getTime() + 1000) : new Date());
    }, 1000);

    return () => {
      clearInterval(interval);
      camera.stopCamera();
    };
  }, [camera.initCamera, gps.getLocation, camera.stopCamera]);

  // Handle school selection for pengawas
  const handleSekolahChange = (sekolahId) => {
    setSelectedSekolahId(sekolahId);
    const selected = sekolahList.find(s => s.id === sekolahId);
    setSekolah(selected);
    if (gps.location && selected) {
      const dist = calculateDistance(gps.location.latitude, gps.location.longitude, selected.latitude, selected.longitude);
      // distance is managed by gps hook, but for pengawas we recalculate locally
    }
  };

  // Face verification derived state
  const effectiveFaceDescriptor = faceData?.face_descriptor || offlineFaceDescriptor;
  const hasFaceReference = faceData?.has_reference || (offlineFaceDescriptor && offlineFaceDescriptor.length === 128);

  // Submit attendance handler
  const handleSubmit = async () => {
    if (!camera.photo) { toast.error('Silakan ambil foto terlebih dahulu'); return; }
    if (!gps.location) { toast.error('Lokasi GPS diperlukan untuk absensi'); return; }
    if (isPengawas && !selectedSekolahId) { toast.error('Pilih sekolah terlebih dahulu'); return; }
    if (gps.location.accuracy > GPS_CONFIG.MAX_ACCURACY_METERS) {
      toast.error(`Akurasi GPS terlalu rendah (${Math.round(gps.location.accuracy)}m). Refresh GPS dan coba di tempat terbuka.`);
      return;
    }
    if (!isMobileDevice && gps.location.accuracy > 500) {
      toast.error('Absensi dari desktop tidak direkomendasikan. Gunakan perangkat mobile dengan GPS.');
      return;
    }

    // Block if native mock GPS detected with high risk
    if (gps.mockDetection && !gps.mockDetection.isSafe && gps.mockDetection.riskScore >= GPS_CONFIG.MOCK_BLOCK_THRESHOLD) {
      toast.error('ABSENSI DIBLOKIR: Fake GPS terdeteksi!', {
        duration: 10000,
        description: `Nonaktifkan aplikasi Fake GPS dan coba lagi. (Risk: ${gps.mockDetection.riskScore}/100)`
      });
      return;
    }

    if (hasFaceReference && !faceVerified) {
      setShowFaceVerification(true);
      toast.info(isOnline ? 'Verifikasi wajah diperlukan sebelum absensi' : 'Verifikasi wajah offline diperlukan sebelum absensi', {
        description: 'Pastikan wajah Anda terlihat jelas di kamera'
      });
      return;
    }

    await submitAttendance(faceVerified, faceMatchScore);
  };

  // Face verification result handler
  const handleFaceVerificationComplete = async (result) => {
    if (result.success) {
      setFaceVerified(true);
      setFaceMatchScore(result.confidence || null);
      setShowFaceVerification(false);

      const livenessSkipped = result.liveness_skipped || false;
      const livenessVerified = result.liveness_verified || false;

      if (livenessSkipped) {
        toast.warning('Wajah terverifikasi, tapi verifikasi liveness dilewati', {
          icon: <ShieldCheck className="w-5 h-5 text-amber-600" />,
          description: 'Absensi akan dicatat dengan security flag'
        });
      } else {
        toast.success('Wajah terverifikasi! Melanjutkan absensi...', {
          icon: <ShieldCheck className="w-5 h-5 text-emerald-600" />
        });
      }

      await submitAttendance(true, result.confidence, livenessSkipped, livenessVerified);
    } else {
      toast.error(result.message || 'Verifikasi wajah gagal');
    }
  };

  // Actual attendance submission
  const submitAttendance = async (verifiedNow = false, matchScore = null, livenessSkipped = false, livenessVerified = false) => {
    setSubmitting(true);
    const finalFaceVerified = verifiedNow || faceVerified || !faceData?.has_reference;
    const finalMatchScore = matchScore || faceMatchScore;

    try {
      const compressedPhoto = await compressImage(camera.photo, 800, 0.7);
      const loc = gps.location;

      const absensiData = {
        foto_base64: compressedPhoto,
        latitude: loc.latitude,
        longitude: loc.longitude,
        akurasi_gps: loc.accuracy,
        tipe: absenType,
        device_type: isMobileDevice ? 'mobile' : 'desktop',
        gps_timestamp: loc.timestamp,
        face_verified: finalFaceVerified,
        liveness_verified: livenessVerified,
        liveness_skipped: livenessSkipped,
        gps_sample_count: loc.sampleCount || null,
        gps_accuracy_variance: loc.accuracyVariance || null,
        gps_has_altitude: loc.hasAltitude,
        fake_gps_score: loc.fakeGpsScore || 0,
        // Native mock GPS detection data
        native_mock_safe: loc.nativeMockDetection?.isSafe ?? null,
        native_mock_risk_score: loc.nativeMockDetection?.riskScore ?? null,
        native_mock_apps_found: loc.nativeMockDetection?.mockAppsFound ?? null,
        native_mock_setting_enabled: loc.nativeMockDetection?.mockSettingEnabled ?? null,
        native_root_detected: loc.nativeMockDetection?.rootDetected ?? null,
        gps_source: loc.source || 'browser',
      };

      if (isPengawas && selectedSekolahId) absensiData.sekolah_id = selectedSekolahId;

      if (isOnline) {
        const response = await createAbsensi(absensiData);
        const statusLabels = {
          'hadir_tepat_waktu': { text: 'Tepat Waktu!', color: 'success' },
          'hadir_terlambat': { text: 'Terlambat', color: 'warning' },
          'mangkir': { text: 'Di Luar Jam Kerja', color: 'warning' }
        };
        const statusInfo = statusLabels[response.status] || { text: 'Tercatat', color: 'success' };

        if (response.status === 'hadir_tepat_waktu') {
          toast.success(`Absensi ${absenType} berhasil - ${statusInfo.text}`, {
            description: finalFaceVerified ? 'Wajah terverifikasi' : undefined
          });
        } else {
          toast.warning(`Absensi ${absenType} tercatat - ${statusInfo.text}`, {
            description: `Status: ${response.status === 'hadir_terlambat' ? 'Terlambat' : 'Di luar jam kerja'}`
          });
        }
      } else {
        const offlineRecord = {
          ...absensiData,
          offline_timestamp: new Date().toISOString(),
          waktu_absen: new Date().toISOString(),
          face_verified_offline: finalFaceVerified,
          face_match_score_offline: finalMatchScore,
          face_verification_method: finalFaceVerified ? 'client_side' : 'none'
        };
        await addToAttendanceQueue(offlineRecord);
        const pending = await getPendingCount();

        setTodayStatus(prev => ({
          ...prev,
          has_masuk: prev?.has_masuk || absenType === 'masuk',
          has_pulang: prev?.has_pulang || absenType === 'pulang',
          message: absenType === 'masuk'
            ? 'Anda sudah absen masuk (data offline). Silakan absen pulang.'
            : 'Anda sudah absen masuk dan pulang hari ini (data offline)',
          is_offline_data: true
        }));

        toast.success(`Absensi ${absenType} disimpan offline. ${pending} menunggu sinkronisasi.`, {
          description: finalFaceVerified
            ? `Wajah terverifikasi secara offline${finalMatchScore ? ` (${finalMatchScore.toFixed(0)}%)` : ''}`
            : 'Verifikasi wajah tidak tersedia'
        });
      }

      navigate('/dashboard');
    } catch (error) {
      console.error('Submit error:', error);
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object' && detail?.code === 'FACE_REGISTRATION_REQUIRED') {
        toast.error(detail.message, { duration: 8000 });
        setFaceRegistrationStatus({ has_face: false, grace_period_expired: true, days_remaining: 0 });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Gagal menyimpan absensi');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Location name for watermark
  const locationName = isPegawaiInstansi ? instansi?.nama : sekolah?.nama;

  // Determine face registration block/warning
  const isGracePeriodExpired = faceRegistrationStatus && !faceRegistrationStatus.has_face && faceRegistrationStatus.grace_period_expired;
  const gracePeriodDaysRemaining = faceRegistrationStatus && !faceRegistrationStatus.has_face && !faceRegistrationStatus.grace_period_expired
    ? faceRegistrationStatus.days_remaining
    : undefined;

  // HARD BLOCK: If face not registered and grace period expired
  if (isGracePeriodExpired) {
    return <FaceRegistrationBlock daysRemaining={0} />;
  }

  // If showing face verification modal
  if (showFaceVerification) {
    return (
      <FaceVerificationScreen
        isOnline={isOnline}
        effectiveFaceDescriptor={effectiveFaceDescriptor}
        onComplete={handleFaceVerificationComplete}
        onCancel={() => setShowFaceVerification(false)}
      />
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-lg mx-auto" data-testid="absensi-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-indigo-950">Absensi</h1>
          <p className="text-gray-500">Ambil foto untuk absensi</p>
        </div>
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* Alert Banners */}
      {gracePeriodDaysRemaining !== undefined && (
        <FaceRegistrationBlock daysRemaining={gracePeriodDaysRemaining} />
      )}
      <FaceVerificationBadge
        hasFaceReference={hasFaceReference}
        faceVerified={faceVerified}
        faceMatchScore={faceMatchScore}
        isOnline={isOnline}
        offlineFaceDescriptor={offlineFaceDescriptor}
      />
      <DesktopWarning isMobileDevice={isMobileDevice} />
      <OfflineWarning isOnline={isOnline} />
      <FakeGpsAlert
        fakeGpsWarning={gps.fakeGpsWarning}
        fakeGpsIndicators={gps.fakeGpsIndicators}
        fakeGpsScore={gps.location?.fakeGpsScore}
      />
      <TodayStatusAlert todayStatus={todayStatus} />

      {/* Masuk/Pulang Toggle */}
      <AbsenTypeSelector
        absenType={absenType}
        setAbsenType={setAbsenType}
        todayStatus={todayStatus}
        loadingStatus={loadingStatus}
      />

      <DuplicateWarnings todayStatus={todayStatus} absenType={absenType} />

      {/* School Selector for Pengawas */}
      {isPengawas && <SchoolSelector sekolahList={sekolahList} selectedSekolahId={selectedSekolahId} onSekolahChange={handleSekolahChange} />}

      {/* Camera/Photo */}
      <CameraSection
        videoRef={camera.videoRef}
        canvasRef={camera.canvasRef}
        photo={camera.photo}
        facingMode={camera.facingMode}
        serverTime={serverTime}
        location={gps.location}
        user={user}
        locationName={locationName}
        onTakePhoto={camera.takePhoto}
        onToggleCamera={camera.toggleCamera}
      />

      {/* GPS Location */}
      <GpsSection
        location={gps.location}
        locationError={gps.locationError}
        gettingLocation={gps.gettingLocation}
        gpsRetryCount={gps.gpsRetryCount}
        gpsWarning={gps.gpsWarning}
        locationTimestamp={gps.locationTimestamp}
        distance={gps.distance}
        inRadius={gps.inRadius}
        isGpsValid={gps.isGpsValid}
        isDesktopWarning={gps.isDesktopWarning}
        isPegawaiInstansi={isPegawaiInstansi}
        onRefresh={gps.getLocation}
        mockDetection={gps.mockDetection}
      />

      {/* Server Time */}
      <ServerTimeCard serverTime={serverTime} />

      {/* Action Buttons */}
      <div className="space-y-3">
        <ActionButtons
          photo={camera.photo}
          isGpsValid={gps.isGpsValid}
          location={gps.location}
          submitting={submitting}
          absenType={absenType}
          onSubmit={handleSubmit}
          onRetake={camera.retakePhoto}
        />
      </div>

      {/* GPS Security Tips */}
      <GpsSecurityTips />
    </div>
  );
};

export default AbsensiPage;
